import type { Pose } from '../controls/types';
import type { HudState, HudModel, HudIcon } from './types';

const MODE_LABELS: Record<string, string> = {
  walk: 'Walking',
  fly: 'Flying',
  boat: 'Rowing',
  transition: 'Transition',
};

const TIER_LABELS: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

function getIcon(state: HudState): HudIcon {
  if (state.atPainterView) {
    return 'painter';
  }
  if (state.mode === 'fly' || state.mode === 'transition') {
    return 'fly';
  }
  return state.mode === 'boat' ? 'boat' : 'walk';
}

function getModeLabel(state: HudState): string {
  if (state.atPainterView) {
    return "Painter's view";
  }
  return MODE_LABELS[state.mode] ?? state.mode;
}

function getSpeedText(state: HudState): string | null {
  if (state.mode !== 'fly' || state.atPainterView) {
    return null;
  }
  return `${Math.round(state.flySpeed)} m/s`;
}

function getTierText(state: HudState): string {
  return TIER_LABELS[state.tier] ?? state.tier;
}

const POINTER_HINTS: Readonly<Record<HudState['pointerLock'], string>> = {
  locked: 'Press H for help',
  unlocked: 'Click or drag to look around',
  refused: 'Drag to look around',
};

function getHint(state: HudState): string {
  if (state.helpOpen) {
    return 'Press H or Esc to close help';
  }
  if (state.mode === 'transition') {
    return 'Press H for help';
  }
  return POINTER_HINTS[state.pointerLock];
}

const HALF_TURN_DEG = 180;

/* One decimal, and no "-0.0" for a number that rounds to nothing. */
function tenths(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return (rounded === 0 ? 0 : rounded).toFixed(1);
}

/**
 * A pose as the address's `pose` parameter takes it (app/urlOptions.ts): layout x and y in metres,
 * height, then yaw and pitch in degrees. What is read off the screen can be put in an address, and
 * it names a place in the layout's own coordinates.
 */
function getCoordsText(pose: Pose | null): string | null {
  if (!pose) return null;
  const degrees = (radians: number): number => (radians * HALF_TURN_DEG) / Math.PI;
  const turn = degrees(pose.yaw) % (2 * HALF_TURN_DEG);
  const yaw =
    turn > HALF_TURN_DEG
      ? turn - 2 * HALF_TURN_DEG
      : turn <= -HALF_TURN_DEG
        ? turn + 2 * HALF_TURN_DEG
        : turn;
  const [x, height, z] = pose.position;
  return [x, -z, height, yaw, degrees(pose.pitch)].map(tenths).join(',');
}

export function toHudModel(state: HudState): HudModel {
  return {
    icon: getIcon(state),
    modeLabel: getModeLabel(state),
    speedText: getSpeedText(state),
    tierText: getTierText(state),
    hint: getHint(state),
    coordsText: getCoordsText(state.coords),
  };
}
